import { CSSProperties, FC, ReactNode, useMemo } from 'react';

import cx from 'clsx';
import { RiLayoutRight2Line } from 'react-icons/ri';

import {
  Box,
  Drawer as MantineDrawer,
  DrawerProps as MantineDrawerProps
} from '@mantine/core';

import { ActionIcon, Button } from '../../buttons';
import { Footer } from '../../sections';
import { Title } from '../../typography';
import { useMergeClassNames } from '../../utils';

import drawerClasses from './Drawer.module.css';
import { DrawerOverrideClassNames, DRAWER_Z_INDEX } from './Drawer.styles';

interface DrawerProps extends MantineDrawerProps {
  /**
   * avoid using default header
   */
  useDefaultHeader?: boolean;
  /**
   * The Header for the Drawer.
   */
  header?: ReactNode;
  /**
   * Custom title
   */
  headerTitle?: ReactNode;
  /**
   * Custom header left
   */
  headerLeft?: ReactNode;
  /**
   * Custom header right
   */
  headerRight?: ReactNode;

  /**
   * Fixed footer component
   */
  fixedFooter?: ReactNode;

  /**
   * Override styles on the fixed footer container (e.g. to adjust height)
   */
  footerStyle?: CSSProperties;

  /**
   * remove default focus trap
   */
  defaultFocusTrap?: boolean;

  /**
   * Remove all default padding
   */
  noPadding?: boolean;

  /**
   * set the content background
   */
  contentBg?: 'white' | 'gray';
}

// A Drawer is side panel. The panel can appear from any side of the screen
const Drawer: FC<DrawerProps> = ({
  opened,
  children,
  withCloseButton,
  fixedFooter,
  footerStyle,
  classNames,
  size = 'md',
  defaultFocusTrap = true,
  header,
  headerTitle,
  headerLeft,
  headerRight,
  useDefaultHeader = true,
  noPadding,
  contentBg = 'white',
  position,
  onClose,
  zIndex = DRAWER_Z_INDEX,
  withOverlay,
  ...args
}) => {
  const contentHeight = `calc(${
    position === 'left' || position === 'right' ? `100vh` : 'var(--drawer-height)'
  }${
    (useDefaultHeader ? ' - var(--drawer-header-height)' : ' - 0px') +
    (fixedFooter ? ' - var(--drawer-footer-height)' : ' - 0px')
  })`;

  const classes = useMemo(() => {
    return DrawerOverrideClassNames({ position, noPadding, contentBg });
  }, [position]);

  const Header = useMemo(() => {
    return header ? (
      header
    ) : useDefaultHeader ? (
      <Box
        className={drawerClasses.headerContainer}
        style={{
          justifyContent:
            (headerLeft || headerRight || withCloseButton) && headerTitle
              ? 'space-between'
              : 'start'
        }}
      >
        {!headerLeft ? (
          <Button
            color="secondary"
            variant="filled"
            onClick={onClose}
            w={92}
            px={'sm'}
          >
            Close
          </Button>
        ) : (
          headerLeft
        )}
        {typeof headerTitle === 'string' ? (
          <Title
            size="md"
            ml={!withCloseButton ? 'md' : 0}
            style={{ textAlign: 'center' }}
          >
            {headerTitle}
          </Title>
        ) : (
          headerTitle
        )}
        {headerRight ? headerRight : !withCloseButton ? <Box w={80} /> : null}
        {withCloseButton ? (
          <Box className={drawerClasses.closeContainer} ml={headerRight ? 4 : 0}>
            <ActionIcon
              onClick={onClose}
              color="secondary"
              variant="subtle"
              aria-label="Close"
            >
              <RiLayoutRight2Line size={20} />
            </ActionIcon>
          </Box>
        ) : null}
      </Box>
    ) : null;
  }, [
    header,
    useDefaultHeader,
    headerLeft,
    headerRight,
    headerTitle,
    withCloseButton,
    onClose
  ]);

  const mergedClassNames = useMergeClassNames({
    classNames: [classes, classNames]
  });

  return (
    <MantineDrawer
      {...args}
      size={size}
      zIndex={zIndex}
      withOverlay={withOverlay}
      position={position}
      opened={opened}
      onClose={onClose}
      classNames={mergedClassNames}
      withCloseButton={false}
      title={Header}
      padding={0}
    >
      {defaultFocusTrap && <input style={{ display: 'none' }} data-autofocus />}
      <Box
        data-testid="drawer-body"
        component="div"
        className={cx(drawerClasses.customBody, {
          [drawerClasses.customBodyWithoutPadding]: noPadding,
          [drawerClasses.customBodyWhite]: contentBg === 'white'
        })}
        style={{
          height: contentHeight
        }}
      >
        {children}
      </Box>
      {fixedFooter && (
        <Footer fixed py={16} px={20} style={footerStyle}>
          {fixedFooter}
        </Footer>
      )}
    </MantineDrawer>
  );
};

export type { DrawerProps };
export { Drawer };
